import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { BookOpen, Building2, GraduationCap } from "lucide-react";

export default function Humanities({ 
  accent = "#115e32",
  pageTitle = "School of Arts and Social Science | University of Creative Technology Chittagong",
}) {
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
	const dLinks = [
		{ label: "Department of English Language and Literature", link:"/department/ell" },
		{ label: "Department of Public Health", link: "/department/dph" },
		{ label: "Department of Islamic Studies", link: "/department/dis" },		
	];
	const pLinks = [
		{ label: "B.A. (Hons) in ELL", link:"/programs/english" },
		{ label: "Master of Public Health", link: "/programs/mph" },
		{ label: "B.A. (Hons) in IS", link: "/programs/bis" },
		{ label: "Master of Islamic Studies", link: "/programs/mis" },	
	];
  return (
	<>
    <section className="relative flex items-center justify-center min-h-screen bg-gradient-to-b from-[#6BCCF2] to-teal-50 text-[#014864] overflow-hidden">
      <div className="w-full max-w-7xl px-6 md:px-12 flex flex-col md:flex-row items-center gap-12">
        {/* Left Column */}
        <div className="flex flex-col items-start md:items-start gap-4 text-left md:w-[65%]">
          <div className="w-full h-40 md:w-48 md:h-48 rounded-2xl flex items-center justify-center bg-gray-100/60 backdrop-blur-md shadow-lg">
            <img
              src="/img/logo.png"
              alt="UCTC logo"
              className="w-[85%] h-[85%] object-contain"
            />
          </div>
          <p className="text-[#014864] font-bold tracking-widest text-xl md:text-2xl">
            UNIVERSITY OF CREATIVE TECHNOLOGY CHITTAGONG
          </p>
          <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 leading-tight">
             School of Arts and Social Science
            <br />
            <span className="text-[#b88f18] font-bold opacity-85">
              Through Innovation & Adoption of Technology
            </span>
          </h1>
          
          <div className="flex gap-4 mt-4">
            <a
              href="/apply-online"
              className="px-5 py-3 rounded-lg bg-gradient-to-r from-[#014864] to-[#6f6016] text-white font-semibold shadow-lg hover:scale-105 transition"
            >
              Apply Now
            </a>
            <a
              href="#about"
              className="px-5 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
            >
              Learn More
            </a>
          </div>
        </div>

        {/* Right Column with Animated SVG */}
        <div className="hidden lg:block flex-1 w-full h-[400px] md:h-[500px]">
          <motion.svg
            viewBox="0 0 1100 600"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-full"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2 }}
          >
            <defs>
              <linearGradient id="g1" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="#e0f2f1" />
                <stop offset="100%" stopColor="#b2dfdb" />
              </linearGradient>
              <linearGradient id="gold" x1="0" x2="1">
                <stop offset="0%" stopColor="#b88f18" />
                <stop offset="100%" stopColor="#014864" />
              </linearGradient>
            </defs>

            {/* Animated Blob */}
            <motion.path
              d="M20,300 C60,120 260,20 460,80 C760,160 840,420 560,460 C380,490 160,420 20,300z"
              fill="url(#g1)"
              animate={{
                d: [
                  "M20,300 C60,120 260,20 460,80 C760,160 840,420 560,460 C380,490 160,420 20,300z",
                  "M10,320 C120,100 260,40 520,100 C780,170 820,420 560,480 C360,510 140,430 10,320z",
                  "M20,300 C60,120 260,20 460,80 C760,160 840,420 560,460 C380,490 160,420 20,300z",
                ],
              }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Floating Dots */}
            <motion.circle cx="120" cy="40" r="10" fill="#a5d6a7" animate={{ cy: [40, 20, 40] }} transition={{ duration: 5, repeat: Infinity }} />
            <motion.circle cx="40" cy="-10" r="6" fill="#80cbc4" animate={{ cy: [-10, 10, -10] }} transition={{ duration: 3.6, repeat: Infinity }} />
            <motion.circle cx="-80" cy="-20" r="8" fill="#4db6ac" animate={{ cy: [-20, -40, -20] }} transition={{ duration: 4.5, repeat: Infinity }} />

            {/* Decorative line */}
            <motion.rect
              x="60"
              y="520"
              height="8"
              rx="4"
              fill="url(#gold)"
              initial={{ width: 0 }}
              animate={{ width: 420 }}
              transition={{ duration: 2, ease: "easeOut" }}
            />

            {/* Badge */}
            <g transform="translate(680,480)" opacity="0.95">
              <rect
                width="300"
                height="60"
                rx="12"
                fill="rgba(0,0,0,0.02)"
                stroke="rgba(0,0,0,0.05)"
              />
              <text
                x="18"
                y="36"
                fontFamily="Inter, Arial"
                fontSize="26"
                fill="#014864"
                fontWeight="700"
              >
                Innovation · Creativity · Change
              </text>
            </g>
          </motion.svg>
        </div>
      </div>
    </section>
	<section className="bg-gradient-to-b from-[#E6FAFE] to-teal py-16 px-6 md:px-12">
      {/* Header */}
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: -30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-4xl font-bold text-[#016C94] mb-3">
          School of Arts and Social Science
        </h2>
        <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
      </motion.div>

      {/* About Section */}
      <motion.div
        className="max-w-5xl mx-auto text-center mb-16"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <p className="text-gray-700 text-lg leading-relaxed text-justify">
          Faculty of Humanities give you opportunity to learn to communicate, to solve problems and to think critically and analytically. Humanities graduates are highly skilled and able to work in almost any ​field. The private sector is constantly on the look-out for successful graduates to take up positions in middle management because it values the proficiency in problem-solving and communications that a student develops while studying for a BA. The ability to think critically and imaginatively and to convey ideas clearly is valued in any profession. The Faculty of Humanities strives to: ​train high-level Humanities students by ensuring that the teaching programmes are internationally competitive and locally relevant, produce outstanding teaching and research outputs in the Humanities by employing the best possible academics and supporting staff and developing and utilising them to their full potential; ​apply management principles, guidelines, processes and practices to maintain and improve excellent academic achievements in the field of teaching and research; ensure by our achievements and services that values are strived at to the benefit of the individual, the University and the community we serve;​ significantly enrich students with the unique added values offered by studies in the Humanities.
        </p>
      </motion.div>

      {/* Department Section */}
      <motion.div
        className="max-w-6xl mx-auto mb-16"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <h3 className="text-2xl font-semibold text-[#016C94] flex items-center gap-2 mb-6">
          <Building2 className="text-[#016C94]" /> Departments
        </h3>

        <div className="grid md:grid-cols-2 gap-4">
          {dLinks.map((dept, index) => (
            <a
              key={index}
              href={dept.link}
              className="p-5 bg-white shadow-md rounded-xl border border-gray-100 hover:border-blue-500 transition-all hover:shadow-lg flex items-center gap-3"
            >
              <BookOpen className="text-[#016C94]" />
              <span className="font-medium text-gray-800 hover:text-blue-700">
                {dept.label}
              </span>
            </a>
          ))}
        </div>
      </motion.div>

      {/* Offered Programs */}
      <motion.div
        className="max-w-6xl mx-auto"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <h3 className="text-2xl font-semibold text-[#016C94] flex items-center gap-2 mb-6">
          <GraduationCap className="text-[#016C94]" /> Offered Programs
        </h3>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {pLinks.map((program, index) => (
            <a
              key={index}
              href={program.link}
              className="bg-white shadow-md rounded-xl p-4 text-center font-medium border border-gray-100 hover:shadow-lg hover:border-blue-500 transition-all"
            >
              {program.label}
            </a>
          ))}
        </div>
      </motion.div>
    </section>
	</>
  );
}
